import React, { useContext, useState, useRef, useEffect } from 'react'
import cn from 'classnames'
import PropTypes from 'prop-types'
import { createPortal } from 'react-dom'
import { usePopper } from 'react-popper'
import { TooltipContext } from '../Tooltip'
import { PROP_TYPE_CHILDREN } from '../constants'
import { getClosest } from '../helpers'

const THEME_DARK = 'dark'
const THEME_LIGHT = 'light'

export interface TooltipContentProps
  extends React.HTMLAttributes<HTMLDivElement> {
  show?: boolean
}

const TooltipContentControlled = ({
  children,
  className,
  show,
  ...props
}: TooltipContentProps) => {
  const {
    tooltipElement,
    setTooltipElement,
    toggleElement,
    arrowElement,
    setArrowElement,
    placement,
    positionFixed,
  } = useContext(TooltipContext)
  const [themeChecked, setThemeChecked] = useState(false)
  const [theme, setTheme] = useState(THEME_DARK)
  const placeholderRef = useRef<HTMLDivElement>(null)
  const { styles, attributes, update } = usePopper(
    toggleElement,
    tooltipElement,
    {
      placement,
      strategy: positionFixed ? 'fixed' : 'absolute',
      modifiers: [
        {
          name: 'arrow',
          options: {
            element: arrowElement,
          },
        },
        {
          name: 'offset',
          options: {
            offset: [0, 5],
          },
        },
      ],
    },
  )

  useEffect(() => {
    if (update) update()
  }, [show, update])

  useEffect(() => {
    const themeEl = getClosest(placeholderRef?.current, '[class*="mc-theme-"]')

    if (themeEl?.className?.includes('mc-theme-light')) {
      setTheme(THEME_LIGHT)
    }

    setThemeChecked(true)
  }, [])

  const renderToLocation = (content: JSX.Element) => {
    if (!show) return null
    return createPortal(
      content,
      ((toggleElement as Element | null)?.parentNode as Element) ||
        document.body,
    )
  }

  const classes = cn({
    'mc-tooltip': true,
    'mc-tooltip--show': show,
    'mc-theme-light': theme === THEME_LIGHT,
    [className ?? '']: className,
  })

  return (
    <>
      {!themeChecked && <div ref={placeholderRef} />}

      {renderToLocation(
        <div
          className={classes}
          ref={setTooltipElement}
          style={styles.popper}
          {...attributes.popper}
          {...props}
        >
          <div className='mc-tooltip__content'>{children}</div>
          <div
            className='mc-tooltip__arrow'
            ref={setArrowElement}
            style={styles.arrow}
          />
        </div>,
      )}
    </>
  )
}

TooltipContentControlled.propTypes = {
  children: PROP_TYPE_CHILDREN,
  className: PropTypes.string,
  show: PropTypes.bool,
}

export default TooltipContentControlled
